import React from 'react';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { Elements as StripeElements } from '@stripe/react-stripe-js';
import { useQuery } from 'react-apollo';

import RegistrationForm from './components/RegistrationForm';
import RegistrationPhoneVerify from './components/RegistrationPhoneVerify';
import RegistrationPayment from './components/RegistrationPayment';
import useStripePromise from '../../../../hooks/useStripePromise';
import useShutterbugRegistration, {
  REGISTRATION_STEPS,
} from './useShutterbugRegistration';
import SignupAlbumLayout from '../../../../components/global/SignupAlbumLayout';
import { getPhotoSearchCheckoutSearchItemsQuery } from '../../../temp/views/onboarding/PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import PlanSelection from './components/PlanSelection';
import { isValidEmail } from '../../../../util/email';
import { formatPhone, isValidPhone } from '../../../../util/phone';
import RegistrationEmailSent from './components/RegistrationEmailSent';
import EventSKUSelection from './components/EventSKUSelection';
import PressLogos from '../Login/components/PressLogos';

const Registration = () => {
  const stripePromise = useStripePromise();
  const { data: { searchSubscriptionItems = [] } = {} } = useQuery(
    getPhotoSearchCheckoutSearchItemsQuery,
  );
  const {
    plan,
    setPlan,
    step,
    setStep,
    loading,
    onPhoneVerificationRequest,
    onPinVerify,
    onPinCodeResend,
    onCheckout,
    onEmailRegistrationRequest,
    onSendEmailVerification,
    onEventSKUSelected,
    eventType,
    paidPlansOnly,
  } = useShutterbugRegistration();

  const onSubmit = values => {
    if (step === REGISTRATION_STEPS.EVENT_SKU_SELECTION) {
      onEventSKUSelected(values);
    }

    if (
      step === REGISTRATION_STEPS.DETAILS &&
      isValidPhone(values.phone_number)
    ) {
      onPhoneVerificationRequest(values);
    } else if (step === REGISTRATION_STEPS.DETAILS) {
      onEmailRegistrationRequest(values);
    }

    if (step === REGISTRATION_STEPS.PIN_VERIFY) {
      onPinVerify(values);
    }

    if (step === REGISTRATION_STEPS.CHECKOUT) {
      onCheckout(values);
    }
  };

  const onValidate = values => {
    const errors = {};

    switch (step) {
      case REGISTRATION_STEPS.DETAILS: {
        if (!isValidEmail(values.email_address)) {
          errors.email_address = 'Email address is not valid';
        }

        if (
          values.phone_number.replace('+1', '').length > 0 &&
          !isValidPhone(values.phone_number)
        ) {
          errors.phone_number = 'Phone number is not valid';
        }
        break;
      }
      default:
        break;
    }
    return errors;
  };

  return (
    <StripeElements stripe={stripePromise}>
      <Helmet>
        <title>Waldo &ndash; Register</title>
      </Helmet>
      <SignupAlbumLayout.Wrapper>
        <div className="registration-wrapper">
          {step === REGISTRATION_STEPS.PLAN ? (
            <PlanSelection
              searchSubscriptionItems={searchSubscriptionItems}
              onPlanSelected={subscriptionItem => {
                setPlan(subscriptionItem.plan);
                setStep(REGISTRATION_STEPS.DETAILS);
              }}
            />
          ) : (
            <Formik
              onSubmit={onSubmit}
              initialValues={{
                first_name: '',
                last_name: '',
                email_address: '',
                email_address_exists: false,
                phone_number: '+1',
                code: '',
                terms_checked: true,

                // Checkout logic
                stripe_token: null,
                plan,
                plan_id: null,
                eventAddonItems: [],
                selectedEventAddonItemIds: [],
                eventItemId: '',
              }}
              validate={onValidate}
              validateOnBlur={false}
              validateOnChange={false}
              render={({
                values,
                handleSubmit,
                handleChange,
                handleBlur,
                errors,
                touched,
              }) => {
                return (
                  <div className="shutterbug-registration-form-wrapper">
                    {step === REGISTRATION_STEPS.EVENT_SKU_SELECTION && (
                      <EventSKUSelection
                        eventType={eventType}
                        paidPlansOnly={paidPlansOnly}
                        onConfirmSelection={({
                          eventItem,
                          eventAddonItems,
                          selectedEventAddonItemIds,
                        }) => {
                          if (eventItem) {
                            handleChange({
                              target: {
                                name: 'eventItemId',
                                value: eventItem.id,
                              },
                            });
                          } else {
                            handleChange({
                              target: {
                                name: 'selectedEventAddonItemIds',
                                value: selectedEventAddonItemIds,
                              },
                            });
                            handleChange({
                              target: {
                                name: 'eventAddonItems',
                                value: eventAddonItems,
                              },
                            });
                          }

                          handleSubmit();
                        }}
                      />
                    )}
                    {step === REGISTRATION_STEPS.DETAILS && (
                      <>
                        <h3>
                          Welcome to Waldo! <br /> Create an account below.
                        </h3>
                        <RegistrationForm
                          values={values}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          errors={errors}
                          touched={touched}
                          loading={loading}
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:sales@waldophotos.com">
                            Contact sales &rarr;
                          </a>
                        </p>
                        <PressLogos />
                      </>
                    )}

                    {step === REGISTRATION_STEPS.PIN_VERIFY && (
                      <>
                        <h3>
                          Please enter the code sent to: <br />{' '}
                          <span className="text-primary">
                            {formatPhone(values.phone_number)}
                          </span>{' '}
                        </h3>
                        <RegistrationPhoneVerify
                          loading={loading}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          onPinCodeResend={onPinCodeResend}
                          onEmailSignup={() =>
                            onEmailRegistrationRequest(values)
                          }
                          values={values}
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:sales@waldophotos.com">
                            Contact sales &rarr;
                          </a>
                        </p>
                        <PressLogos />
                      </>
                    )}
                    {step === REGISTRATION_STEPS.VERIFICATION_EMAIL && (
                      <>
                        <h3>Email verification sent.</h3>
                        <RegistrationEmailSent
                          values={values}
                          loading={loading}
                          handleSubmit={handleSubmit}
                          handleChange={handleChange}
                          onSendEmailVerification={onSendEmailVerification}
                          onEditEmail={() =>
                            setStep(REGISTRATION_STEPS.DETAILS)
                          }
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:sales@waldophotos.com">
                            Contact sales &rarr;
                          </a>
                        </p>
                        <PressLogos />
                      </>
                    )}

                    {step === REGISTRATION_STEPS.CHECKOUT && (
                      <>
                        <RegistrationPayment
                          values={values}
                          submitting={loading}
                          onCheckout={onSubmit}
                          handleChange={handleChange}
                        />
                        <hr />
                        <p className="mb-4 mt-4 text-sm">
                          Need Help?{' '}
                          <a href="mailto:sales@waldophotos.com">
                            Contact sales &rarr;
                          </a>
                        </p>
                        <PressLogos />
                      </>
                    )}
                  </div>
                );
              }}
            />
          )}
        </div>
      </SignupAlbumLayout.Wrapper>
    </StripeElements>
  );
};

export default Registration;
